import { LayerMapping, LayerType, PolyLine } from "../data/layers";
import { Elements, fetchRawOverpassQuery } from "./overpass";

export type relationNode = {
  id: number,
  tags: Record<string, string>,
  type: string,
  members: wayNode[]
}

export type wayNode = {
  id: number,
  type: string,
  nodes: number[],
  tags: Record<string, string>
}

export type wayCustom = {
  id: number,
  nodes: Array<number | { lat: number; lon: number }>,
  ref: string | number,
  type: string,
}

export type activeRouteType = {
  id: number,
  members: Array<wayCustom | {
    id: number,
    nodes: number[],
    ref: string | number,
    type: string,
  }>,
  tags: Record<string, string>,
  type: string,
}

export type BikeWayClass =
  | "DIRECT_CYCLEWAY"
  | "SHARED_PATH"
  | "SEGREGATED_SHARED_PATH"
  | "CYCLE_LANE"
  | "CYCLE_TRACK"
  | "CONTRAFLOW_LANE"
  | "CONTRAFLOW_MIXED"
  | "BUS_BIKE_LANE"
  | "SHARED_LANE"
  | "BICYCLE_STREET"
  | "CALM_STREET"
  | "CYCLE_SHOULDER"
  | "TERRAIN_ROUTE"
  | "MIXED_TRAFFIC"
  | "X_DISMOUNT_ONLY"
  | "EXCLUDED"
  | "OTHER";

export type BikeWaySurfaceClass =
  | "surface_difficult"
  | "surface_ground"
  | "surface_gravel"
  | "surface_compacted"
  | "surface_paved"
  | "surface_unknown";

export type BikeWayStyle = {
  color: string;
  width: number;
  opacity: number;
  dasharray: string | null;
  innerColor?: string;
  innerWidth?: number;
  innerDasharray?: string | null;
  zIndex: number;
  className: Exclude<BikeWayClass, "EXCLUDED" | "OTHER">;
  surfaceClass: BikeWaySurfaceClass;
};

export const BIKE_WAY_FILTER_OPTIONS: Array<{
  id: Exclude<BikeWayClass, "EXCLUDED" | "OTHER">;
  label: string;
}> = [
  { id: "DIRECT_CYCLEWAY", label: "droga dla rowerów (DDR)" },
  { id: "SHARED_PATH", label: "droga dla pieszych i rowerów — wspólna" },
  { id: "SEGREGATED_SHARED_PATH", label: "droga dla pieszych i rowerów — rozdzielona" },
  { id: "CYCLE_LANE", label: "pas rowerowy" },
  { id: "CYCLE_TRACK", label: "wydzielona DDR wzdłuż jezdni" },
  { id: "CONTRAFLOW_LANE", label: "kontrapas rowerowy" },
  { id: "CONTRAFLOW_MIXED", label: "kontraruch rowerowy na jezdni" },
  { id: "BUS_BIKE_LANE", label: "pas autobusowo-rowerowy" },
  { id: "SHARED_LANE", label: "jezdnia z sierżantami" },
  { id: "BICYCLE_STREET", label: "ulica rowerowa" },
  { id: "CALM_STREET", label: "jezdnia uspokojona (Tempo 30 / strefa)" },
  { id: "CYCLE_SHOULDER", label: "pobocze dostępne dla roweru" },
  { id: "TERRAIN_ROUTE", label: "droga terenowa / szlak" },
  { id: "MIXED_TRAFFIC", label: "zwykła jezdnia — ruch mieszany" },
  { id: "X_DISMOUNT_ONLY", label: "prowadzenie roweru" },
];

export const BIKE_WAY_LOADING_DEFAULTS: Record<string, boolean> = BIKE_WAY_FILTER_OPTIONS.reduce<Record<string, boolean>>(
  (result, option) => {
    result[option.id] = false;
    return result;
  },
  {}
);

type Tags = Record<string, string | undefined>;

const BIKE_WAY_STYLES: Record<Exclude<BikeWayClass, "EXCLUDED" | "OTHER">, Omit<BikeWayStyle, "className" | "surfaceClass">> = {
  DIRECT_CYCLEWAY: {
    color: "#0877C9",
    width: 5.0,
    opacity: 1.0,
    dasharray: null,
    zIndex: 800
  },
  SHARED_PATH: {
    color: "#00A6A6",
    width: 4.4,
    opacity: 1.0,
    dasharray: null,
    zIndex: 780
  },
  SEGREGATED_SHARED_PATH: {
    color: "#00A6A6",
    width: 5.0,
    opacity: 1.0,
    dasharray: null,
    innerColor: "#FFFFFF",
    innerWidth: 1.0,
    innerDasharray: "3,3",
    zIndex: 790
  },
  CYCLE_LANE: {
    color: "#315EFB",
    width: 3.1,
    opacity: 0.95,
    dasharray: null,
    zIndex: 720
  },
  CYCLE_TRACK: {
    color: "#0877C9",
    width: 4.2,
    opacity: 1.0,
    dasharray: null,
    zIndex: 760
  },
  CONTRAFLOW_LANE: {
    color: "#315EFB",
    width: 3.1,
    opacity: 0.95,
    dasharray: null,
    zIndex: 725
  },
  CONTRAFLOW_MIXED: {
    color: "#61758A",
    width: 2.2,
    opacity: 0.85,
    dasharray: null,
    zIndex: 600
  },
  BUS_BIKE_LANE: {
    color: "#7A49A5",
    width: 3.0,
    opacity: 0.95,
    dasharray: null,
    zIndex: 710
  },
  SHARED_LANE: {
    color: "#295E9F",
    width: 2.2,
    opacity: 0.9,
    dasharray: null,
    zIndex: 650
  },
  BICYCLE_STREET: {
    color: "#25804D",
    width: 2.8,
    opacity: 0.95,
    dasharray: null,
    zIndex: 680
  },
  CALM_STREET: {
    color: "#4E8E42",
    width: 2.0,
    opacity: 0.8,
    dasharray: null,
    zIndex: 500
  },
  CYCLE_SHOULDER: {
    color: "#C88600",
    width: 2.4,
    opacity: 0.9,
    dasharray: null,
    zIndex: 580
  },
  TERRAIN_ROUTE: {
    color: "#8B6B46",
    width: 2.2,
    opacity: 0.85,
    dasharray: null,
    zIndex: 450
  },
  MIXED_TRAFFIC: {
    color: "#718096",
    width: 1.1,
    opacity: 0.55,
    dasharray: null,
    zIndex: 250
  },
  X_DISMOUNT_ONLY: {
    color: "#D62728",
    width: 0.9,
    opacity: 0.45,
    dasharray: "1,5",
    zIndex: 180
  }
};

export function getBikeWayLegendStyle(
  bikeWayClass: Exclude<BikeWayClass, "EXCLUDED" | "OTHER">
) {
  return BIKE_WAY_STYLES[bikeWayClass];
}

const BIKE_WAY_QUERY_PARTS: Array<{
  classes: Array<Exclude<BikeWayClass, "EXCLUDED" | "OTHER">>;
  query: string;
}> = [
  {
    classes: ["DIRECT_CYCLEWAY", "SHARED_PATH", "SEGREGATED_SHARED_PATH", "TERRAIN_ROUTE"],
    query: `
    [out:json][timeout:60];
    area[name="Sosnowiec"]->.sosnowiec;
    (
      relation(area.sosnowiec)[type="route"][route="bicycle"];
      way(area.sosnowiec)[highway="cycleway"];
      way(area.sosnowiec)[highway~"^(path|footway|pedestrian)$"][bicycle~"^(yes|designated|permissive)$"];
    );
    out;
    >;
    out skel qt;
  `,
  },
  {
    classes: ["CYCLE_LANE", "CYCLE_TRACK", "CONTRAFLOW_LANE", "CONTRAFLOW_MIXED", "BUS_BIKE_LANE", "SHARED_LANE", "BICYCLE_STREET", "CYCLE_SHOULDER"],
    query: `
    [out:json][timeout:60];
    area[name="Sosnowiec"]->.sosnowiec;
    (
      way(area.sosnowiec)[highway](if:
        t["cycleway"] != "" ||
        t["cycleway:left"] != "" ||
        t["cycleway:right"] != "" ||
        t["cycleway:both"] != "" ||
        t["oneway:bicycle"] == "no"
      );
      way(area.sosnowiec)[highway="living_street"];
    );
    out;
    >;
    out skel qt;
  `,
  },
  {
    classes: ["CALM_STREET"],
    query: `
    [out:json][timeout:60];
    area[name="Sosnowiec"]->.sosnowiec;
    (
      way(area.sosnowiec)[highway="living_street"];
      way(area.sosnowiec)[highway~"^(residential|service|unclassified|tertiary|secondary|primary)$"](if:
        t["maxspeed"] == "10" || t["maxspeed"] == "20" || t["maxspeed"] == "30" ||
        t["zone:maxspeed"] == "PL:10" || t["zone:maxspeed"] == "PL:20" || t["zone:maxspeed"] == "PL:30" ||
        t["source:maxspeed"] == "PL:zone30" || t["source:maxspeed"] == "PL:zone"
      );
    );
    out;
    >;
    out skel qt;
  `,
  },
  {
    classes: ["TERRAIN_ROUTE"],
    query: `
    [out:json][timeout:60];
    area[name="Sosnowiec"]->.sosnowiec;
    (
      way(area.sosnowiec)[highway="track"];
      way(area.sosnowiec)[highway~"^(track|path)$"](if:
        t["surface"] == "unpaved" || t["surface"] == "compacted" || t["surface"] == "fine_gravel" ||
        t["surface"] == "gravel" || t["surface"] == "pebblestone" || t["surface"] == "ground" ||
        t["surface"] == "dirt" || t["surface"] == "earth" || t["surface"] == "grass" ||
        t["surface"] == "mud" || t["surface"] == "sand" || t["surface"] == "woodchips" ||
        t["tracktype"] != ""
      );
    );
    out;
    >;
    out skel qt;
  `,
  },
  {
    classes: ["MIXED_TRAFFIC"],
    query: `
    [out:json][timeout:60];
    area[name="Sosnowiec"]->.sosnowiec;
    (
      way(area.sosnowiec)[highway~"^(residential|service|unclassified|tertiary|secondary|primary)$"]
        ["motorroad"!="yes"]
        ["bicycle"!~"^(no|dismount|private)$"]
        (if:
          t["cycleway"] == "" &&
          t["cycleway:left"] == "" &&
          t["cycleway:right"] == "" &&
          t["cycleway:both"] == ""
        );
    );
    out;
    >;
    out skel qt;
  `,
  },
];

function mergeOverpassElements(chunks: Elements[][]): any[] {
  const mergedElements = new Map<string, any>();

  chunks.forEach((chunk) => {
    chunk.forEach((element: any) => {
      if (!element?.type || element?.id === undefined) {
        return;
      }

      const key = `${element.type}:${element.id}`;
      const existingElement = mergedElements.get(key);

      if (!existingElement) {
        mergedElements.set(key, element);
        return;
      }

      mergedElements.set(key, {
        ...existingElement,
        ...element,
        tags: {
          ...(existingElement.tags || {}),
          ...(element.tags || {}),
        },
        members: element.members || existingElement.members,
        nodes: element.nodes || existingElement.nodes,
      });
    });
  });

  return Array.from(mergedElements.values());
}

function buildBikeWaysState(elems: any[]) {
  const nodesArray = elems
    .filter((el) => el?.type === "node")
    .map((el: any) => [el?.id, { lat: el?.lat, lon: el?.lon }] as [number, { lat: number; lon: number }]);

  const waysArray = elems.filter((el: any) => el?.type === "way");
  const relationsArray = elems.filter((el: any) => el?.type === "relation") as unknown as activeRouteType[];
  const waysById = new Map<number, any>(
    waysArray.map((el: any) => [el?.id, el] as [number, any])
  );
  const nodesById = new Map(nodesArray);

  relationsArray.forEach((relation) => {
    relation.members = (relation.members || [])
      .map((way: wayCustom) => {
        const sourceWay: any = waysById.get(Number(way?.ref));

        if (!sourceWay || !Array.isArray(sourceWay.nodes)) {
          return {
            ...way,
            nodes: [],
          };
        }

        return {
          ...way,
          nodes: sourceWay.nodes
            .map((point: number) => nodesById.get(point))
            .filter(Boolean),
        };
      })
      .filter((way: wayCustom) => Array.isArray(way.nodes) && way.nodes.length > 1);
  });

  const newLines: PolyLine[] = waysArray
    .map((way: any) => {
      const tags = way?.tags || {};
      const style = getBikeWayStyle(tags);

      if (!style || !Array.isArray(way?.nodes)) {
        return null;
      }

      const nodes = way.nodes
        .map((point: number) => nodesById.get(point))
        .filter(Boolean);

      if (nodes.length < 2) {
        return null;
      }

      return {
        ...way,
        nodes,
        bikeClass: style.className,
        surfaceClass: style.surfaceClass,
        renderStyle: style,
      } as PolyLine;
    })
    .filter((way): way is PolyLine => way !== null);

  return {
    ways: newLines,
    routes: relationsArray,
  };
}

function hasCyclewayValue(tags: Tags, values: string[]) {
  const keys = ["cycleway", "cycleway:left", "cycleway:right", "cycleway:both"];
  return keys.some((key) => values.includes(tags[key] || ""));
}

function isExcluded(tags: Tags) {
  if (["no", "private"].includes(tags.bicycle || "")) return true;
  if (tags.access === "no") return true;
  if (tags.motorroad === "yes") return true;
  if (["motorway", "motorway_link"].includes(tags.highway || "")) return true;
  return false;
}

function hasDesignatedBikeAndFoot(tags: Tags) {
  return tags.bicycle === "designated" && tags.foot === "designated";
}

function isDirectCycleway(tags: Tags) {
  return tags.highway === "cycleway" || (tags.bicycle === "designated" && tags.foot !== "designated");
}

function isSharedPath(tags: Tags) {
  return ["path", "footway", "pedestrian"].includes(tags.highway || "") && hasDesignatedBikeAndFoot(tags);
}

function isTerrainRoute(tags: Tags) {
  return (
    tags.highway === "track" ||
    (tags.highway === "path" && ["yes", "permissive"].includes(tags.bicycle || ""))
  );
}

function isCalmStreet(tags: Tags) {
  const maxspeed = Number.parseInt(tags.maxspeed || "", 10);
  const hasTrafficCalming =
    tags.highway === "living_street" ||
    [10, 20, 30].includes(maxspeed) ||
    ["PL:10", "PL:20", "PL:30"].includes(tags["zone:maxspeed"] || "") ||
    ["PL:zone", "PL:zone30"].includes(tags["source:maxspeed"] || "");

  return hasTrafficCalming;
}

function isMixedTraffic(tags: Tags) {
  return ["residential", "service", "unclassified", "tertiary", "secondary", "primary"].includes(tags.highway || "");
}

export function classifyBikeWay(tags: Tags): BikeWayClass {
  if (tags.bicycle === "dismount") return "X_DISMOUNT_ONLY";
  if (isExcluded(tags)) {
    return "EXCLUDED";
  }

  if (isSharedPath(tags)) return tags.segregated === "yes" ? "SEGREGATED_SHARED_PATH" : "SHARED_PATH";
  if (isDirectCycleway(tags)) return "DIRECT_CYCLEWAY";
  if (tags.bicycle_road === "yes" || tags.cyclestreet === "yes") return "BICYCLE_STREET";
  if (hasCyclewayValue(tags, ["opposite_lane"])) return "CONTRAFLOW_LANE";
  if (hasCyclewayValue(tags, ["track", "opposite_track"])) return "CYCLE_TRACK";
  if (hasCyclewayValue(tags, ["lane"])) return "CYCLE_LANE";
  if (hasCyclewayValue(tags, ["share_busway"])) return "BUS_BIKE_LANE";
  if (hasCyclewayValue(tags, ["shared_lane"])) return "SHARED_LANE";
  if (hasCyclewayValue(tags, ["shoulder"])) return "CYCLE_SHOULDER";
  if (tags["oneway:bicycle"] === "no") return "CONTRAFLOW_MIXED";
  if (isCalmStreet(tags)) return "CALM_STREET";
  if (isTerrainRoute(tags)) return "TERRAIN_ROUTE";
  if (isMixedTraffic(tags)) return "MIXED_TRAFFIC";

  return "OTHER";
}

export function getSurfaceModifier(tags: Tags) {
  const surface = tags.surface;
  const tracktype = tags.tracktype;

  if (["sand", "mud"].includes(surface || "")) {
    return {
      surfaceClass: "surface_difficult" as BikeWaySurfaceClass,
      dasharray: "1,7",
      opacityMultiplier: 0.75,
      widthDelta: -0.4
    };
  }

  if (
    ["ground", "dirt", "earth", "grass"].includes(surface || "") ||
    ["grade4", "grade5"].includes(tracktype || "")
  ) {
    return {
      surfaceClass: "surface_ground" as BikeWaySurfaceClass,
      dasharray: "3,5,1,5",
      opacityMultiplier: 0.85,
      widthDelta: -0.2
    };
  }

  if (["gravel", "pebblestone"].includes(surface || "") || tracktype === "grade3") {
    return {
      surfaceClass: "surface_gravel" as BikeWaySurfaceClass,
      dasharray: "8,5",
      opacityMultiplier: 0.9,
      widthDelta: 0
    };
  }

  if (
    ["compacted", "fine_gravel"].includes(surface || "") ||
    ["grade1", "grade2"].includes(tracktype || "")
  ) {
    return {
      surfaceClass: "surface_compacted" as BikeWaySurfaceClass,
      dasharray: "12,5",
      opacityMultiplier: 0.95,
      widthDelta: 0
    };
  }

  if (["asphalt", "concrete", "paved", "paving_stones", "sett", "cobblestone"].includes(surface || "")) {
    return {
      surfaceClass: "surface_paved" as BikeWaySurfaceClass,
      dasharray: null,
      opacityMultiplier: 1,
      widthDelta: 0
    };
  }

  return {
    surfaceClass: "surface_unknown" as BikeWaySurfaceClass,
    dasharray: null,
    opacityMultiplier: 1,
    widthDelta: 0
  };
}

export function getBikeWayStyle(tags: Tags): BikeWayStyle | null {
  const cls = classifyBikeWay(tags);

  if (cls === "EXCLUDED" || cls === "OTHER") {
    return null;
  }

  const base = { ...BIKE_WAY_STYLES[cls] };
  const surfaceModifier = getSurfaceModifier(tags);
  if (
    surfaceModifier.surfaceClass !== "surface_paved" &&
    surfaceModifier.surfaceClass !== "surface_unknown"
  ) {
    base.dasharray = surfaceModifier.dasharray || base.dasharray;
    base.opacity = Math.max(0.25, base.opacity * surfaceModifier.opacityMultiplier);
    base.width = Math.max(0.8, base.width + surfaceModifier.widthDelta);

    // The line colour always represents the form of movement. Only the
    // texture, opacity and width describe the surface.
  }

  if (tags.bicycle === "permissive") {
    base.opacity *= 0.8;
  }

  if (cls === "MIXED_TRAFFIC" && tags.highway === "primary") {
    base.opacity = 0.35;
    base.width = 1.0;
  }

  return {
    ...base,
    className: cls,
    surfaceClass: surfaceModifier.surfaceClass
  };
}

export const fetchWays = async (
  setActiveWays: (ways: PolyLine[]) => void,
  setActiveRoutes: (routes: activeRouteType[]) => void,
  newLayer: LayerMapping | undefined,
  onChunkLoaded?: (classes: Array<Exclude<BikeWayClass, "EXCLUDED" | "OTHER">>) => void
) => {
  if (newLayer && newLayer.type === LayerType.WAY) {
    const resultChunks: Elements[][] = [];

    for (let index = 0; index < BIKE_WAY_QUERY_PARTS.length; index += 1) {
      const response = await fetchRawOverpassQuery(BIKE_WAY_QUERY_PARTS[index].query);

      if (typeof response !== "boolean") {
        resultChunks.push(response);
        const mergedElements = mergeOverpassElements(resultChunks);
        const nextState = buildBikeWaysState(mergedElements);
        setActiveWays(nextState.ways);
        setActiveRoutes(nextState.routes);
      }

      onChunkLoaded?.(BIKE_WAY_QUERY_PARTS[index].classes);
    }
    return;
  }

  return Promise.resolve();
}
